import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ducktypeStringMap } from '@iotv/core-services';
import { camelToDisplay, getObjectHash, HistoryStore, ObservationHistory } from '@iotv/datamodel';
import { SortOrder } from '@iotv/iotv-v3-types';
import { RemoveCircle } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { Autocomplete, Box, Button, createFilterOptions, Grid, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import Switch from '@mui/material/Switch';
import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObjectMemo } from '../../..//hooks/useObjectMemo';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { getPseudoVBO } from '../../../data/TypeHelpers';
import { useObservations } from '../../../hooks/useObservations';
import { AdjacentType, ComponentMode, ObjectViewType } from '../../../types/AppTypes';
import { gapiIsPresent } from '../../../util/Scripts/isPresent';
import { useColumnVisibilityForDataGrid } from '../../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { AutosizedPopper } from '../../common/AutosizedPopper';
import { AutosizedTextField } from '../../common/AutosizedTextField';
import { DownloadButton } from '../../common/ViewObjectDownloadButton';
import { StripedDataGrid } from '../../factories/DataGrid';
import { InputFieldArbitrator } from '../../factories/inputFields/InputFieldArbitrator';
import { commonObjectTypeToVOB } from '../../io/GoogleV2Hooks';
import { IOType } from '../../io/types';
import { SimpleIOModal } from '../../io/VOBbased/SimpleIOModal';
import { NavSections } from '../../navigation/NavSections';
import { getExportVOBs } from './export';
import { ducktype, filterKeysByTypeToViewDefinition, metricItemsToVOBs } from './helperFns';
const debug = process.env.REACT_APP_DEBUG && false;
const actualReadingPrefix = 'actual_';
const filter = createFilterOptions();
/**
This needs TimePeriodSelector to reset the new default history store filter on ObjectCard, which prevents size exceeded results
 */
export default function ObservationCard(props) {
    const navigate = useNavigate();
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const { viewObject: viewObjectIn, objectHistory: objectHistoryIn, loadingObjectHistory } = props;
    const initialKeys = undefined;
    debug && console.log('ObservationCard', props);
    const { state: [observationHistory, setObservationHistory], errMessage, working, querying, functions: { addObservation, save, deleteObject }, } = useObservations(viewObjectIn?.matchedPrimary, initialKeys);
    const [objectHistory, setObjectHistory] = React.useState(objectHistoryIn);
    const [knownKeys, setKnownKeys] = React.useState([]);
    const [combined, setCombined] = React.useState(false);
    const [matchedActualsCount, setMatchedActualsCount] = React.useState(undefined);
    const observationHistoryInstance = observationHistory && new ObservationHistory(observationHistory);
    const observationHistoryStore = observationHistoryInstance?.observedHistory;
    debug && console.log('historyStore', observationHistoryStore);
    const historyStoreLastTimestamp = observationHistoryStore?.lastTimestamp();
    const historyStoreLength = observationHistoryStore?.getLength();
    const historyStoreKeysHash = observationHistoryStore?.keys().join('');
    /* EXPERIMENTAL, get the spreadsheet and sheet names from useGoogleIO */
    const fileSelectRef = useRef(observationHistory?.googleSheet);
    const fileSelectRefHash = getObjectHash(fileSelectRef?.current);
    const saveCurried = () => {
        debug && console.log('Save curried has fileselectref', fileSelectRef);
        const observationHistoryClone = { ...observationHistory };
        if (observationHistoryClone?.type) {
            observationHistoryClone.googleSheet = fileSelectRef.current;
            const newObservationHistory = new ObservationHistory(observationHistoryClone);
            setObservationHistory(newObservationHistory);
            save(newObservationHistory);
        }
    };
    const observationKeys = observationHistoryStore?.keys() ?? [];
    const getEmptyObject = () => Object.fromEntries([...observationKeys.map((k) => [k, null]), ['timestamp', Date.now()]]);
    const { state: [stateObject, setStateObject], hash: tableObHash, updateKV, } = useObjectMemo(getEmptyObject());
    useEffect(() => {
        debug && console.log('re rendering on changed matchedPrimary sk');
    }, [observationHistoryInstance?.sk]);
    useEffect(() => {
        debug && console.log('re rendering on changed objectHistory');
        if (objectHistoryIn) {
            setObjectHistory(objectHistoryIn);
            setKnownKeys(objectHistoryIn.recentStateHistory?.keys().sort((a, b) => a.localeCompare(b)) ?? []);
        }
    }, [objectHistoryIn]);
    useEffect(() => {
        debug && console.log('re rendering on changed history store, googleSheet', observationHistory?.googleSheet);
        fileSelectRef.current = observationHistory?.googleSheet;
    }, [historyStoreLastTimestamp, historyStoreLength, historyStoreKeysHash, tableObHash]);
    const observatinStateSnapshots = observationHistoryStore ? observationHistoryStore.getStateSnapshotArray(SortOrder.DESCENDING) : [];
    const pseudoTypeId = `${observationHistoryInstance?.type ?? 'Unkown'}_observation`;
    const observationMetricItems = metricItemsToVOBs({
        psuedoParentVob: observationHistory,
        pseudoTypeId,
        stateSnapshots: observatinStateSnapshots,
    });
    const viewObject = {
        matchedPrimary: observationHistoryInstance,
        matchedRelatedByPk: [],
        matchedRelatedBySk: observationMetricItems,
    };
    const handleDeleteObservation = (gp) => {
        debug && console.log(`handleDeleteObservation got `, gp);
        const key = gp.row.timestamp;
        const idx = observationMetricItems.findIndex((item) => item.timestamp === key);
        debug && console.log('Want to delete ', { idx, observationMetricItems });
        if (observationHistory && observationHistoryStore) {
            const splicedHistoryStore = observationHistoryStore.splice(idx, 1);
            setObservationHistory(new ObservationHistory({ ...observationHistory, observedHistory: splicedHistoryStore }));
        }
        else {
            debug && console.log('Did not splice ', { idx, observationMetricItems });
        }
    };
    const viewDefinition = filterKeysByTypeToViewDefinition(observationHistoryStore?.keys(), observationHistoryStore);
    const adjacentFilter = {
        adjacentType: AdjacentType.CHILD,
        objectType: pseudoTypeId,
    };
    const pk = observationHistoryInstance?.sk ?? 'unknownParentSk';
    const saveButton = (_jsx(Button, { variant: 'contained', startIcon: _jsx(SaveIcon, {}), onClick: saveCurried, disabled: working, children: "Save" }, `${pk}_observationHistoryCard_SaveButton`));
    const downloadButton = (_jsx(DownloadButton, { viewObject, viewDefinition, adjacentFilter }, `${pk}_observationHistoryCard_DownloadButton`));
    const historyStoreKeysPlusTs = observationHistoryStore ? ['timestamp', ...observationHistoryStore.keys()] : [];
    const handleAddKey = (keyName) => {
        if (keyName && observationHistory && observationHistoryStore) {
            const historyStoreClone = new HistoryStore({ ...observationHistoryStore });
            historyStoreClone.addKey(keyName);
            setObservationHistory(new ObservationHistory({ ...observationHistory, observedHistory: historyStoreClone }));
        }
        else {
            debug && console.log(`No keyName or historyStore `);
        }
    };
    const handleRemoveKey = (keyName) => {
        if (keyName && observationHistory && observationHistoryStore) {
            const historyStoreClone = new HistoryStore({ ...observationHistoryStore });
            historyStoreClone.removeKey(keyName);
            const observationHistoryClone = new ObservationHistory({ ...observationHistory, observedHistory: historyStoreClone });
            setObservationHistory(observationHistoryClone);
            debug && console.log(`removed ${keyName}`, observationHistoryClone);
        }
    };
    const ducktypeWrapper = (ob) => {
        const ducktyped = ducktypeStringMap(ob);
        debug && console.log(`Ducktyping`, ob, ducktyped);
        return ducktyped;
    };
    const handleAddObservation = () => {
        const { timestamp, ..._stateObject } = stateObject;
        addObservation({
            timestamp: timestamp ?? Date.now(),
            state: ducktypeWrapper(_stateObject),
        });
        setStateObject(getEmptyObject());
    };
    const handleCombinedOutputChange = () => {
        setCombined(!combined);
    };
    const controls = {
        addControl: (_jsx(Tooltip, { title: 'Add a column from known readings', arrow: true, children: _jsx(Autocomplete, { id: 'knownKeysAutoComplete',
                clearOnBlur: true,
                selectOnFocus: true,
                handleHomeEndKeys: true,
                options: knownKeys,
                size: 'small',
                onChange: (e, v) => {
                    console.log(`addControl Autocomplete`, e, v);
                    handleAddKey(v);
                },
                filterOptions: (options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option);
                    if (inputValue !== '' && !isExisting) {
                        filtered.push(inputValue);
                    }
                    return filtered;
                },
                slots: {
                    popper: AutosizedPopper,
                },
                renderInput: (params) => (_jsx(AutosizedTextField, { label: 'Add column',
                    variant: 'outlined',
                    placeholder: 'Select a column...',
                    onChange: () => { },
                    ...params })) }, 'addControl') })),
        deleteKeyControl: (_jsx(Tooltip, { title: 'Remove column from table', arrow: true, children: _jsx(Autocomplete, { id: 'deleteKeysAutoComplete',
                options: observationHistoryStore?.keys() ?? ['noKeys'],
                size: 'small',
                onChange: (e, v) => {
                    console.log(`deleteControl Autocomplete`, e, v);
                    handleRemoveKey(v);
                },
                slots: {
                    popper: AutosizedPopper,
                },
                renderInput: (params) => (_jsx(AutosizedTextField, { label: 'Remove column',
                    placeholder: 'Select a column...',
                    onChange: () => { },
                    ...params })) }, 'deleteControl') })),
        viewInComparison: (_jsx(_Fragment, { children: viewObjectIn.matchedPrimary && (_jsx(Tooltip, { title: `Open ${viewObjectIn.matchedPrimary.name} in Comparison View`, arrow: true, children: _jsx(Button, { variant: "outlined", onClick: () => navigate(`/${NavSections.COMPARISONVIEW}?comparisonObjectSks=${viewObjectIn.matchedPrimary.sk}`), children: "Compare" }) })) })),
        toggleCombinedControl: (_jsxs(Stack, { direction: 'row', alignItems: 'center', gap: 1, children: [_jsx(Switch, { size: 'small',
                    id: 'modeSwitch',
                    defaultChecked: combined,
                    icons: false,
                    onChange: handleCombinedOutputChange }), _jsx(Typography, { children: !combined ? 'Exporting Observations only' : `Exporting Observations and ${matchedActualsCount !== undefined ? matchedActualsCount : ''} Actuals` })] })),
    };
    const displayObject = { ...getPseudoVBO({ type: pseudoTypeId, id: '', name: '', ...stateObject }) };
    const stateObjectTable = (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [historyStoreKeysPlusTs.map((key) => {
                const viewKeyDefinition = {
                    editable: true,
                    key,
                    type: ducktype(key, stateObject?.[key]),
                    label: key,
                };
                return [
                    _jsx(Stack, { direction: 'row', children: _jsx(InputFieldArbitrator, { keyToUse: key,
                            tableOb: displayObject,
                            mode: ComponentMode.EDIT,
                            key,
                            viewKeyDefinition,
                            updateKV,
                            textFieldProps: {
                                variant: 'outlined',
                                InputLabelProps: {
                                    shrink: true,
                                },
                            } }) }, key),
                ];
            }), _jsx(Tooltip, { title: historyStoreKeysHash === '' ? 'Add one or more columns, then use this control to add values ' : 'Add an observation with these values', arrow: true, children: _jsx("span", { children: _jsx(IconButton, { edge: "end", "aria-label": "addObservation", disabled: historyStoreKeysHash === '', onClick: handleAddObservation, children: _jsx(AddIcon, {}) }) }) })] }));
    const [columnVisibilityModel] = useColumnVisibilityForDataGrid({
        dataTableReference: 'Observations',
        objectTypeId: observationHistoryInstance?.type,
        viewType: ObjectViewType.List,
    });
    const columns = [
        {
            headerName: '',
            field: 'id',
            renderCell: (gp) => _jsx(RemoveCircle, { onClick: () => handleDeleteObservation(gp), sx: { cursor: 'pointer' } }),
            width: 16,
        },
        { field: 'localTimeStr', headerName: 'Time', width: 250 },
        ...observationKeys.map((k) => {
            const colDef = {
                field: k,
                headerName: camelToDisplay(k),
                editable: true,
                flex: 1,
            };
            return colDef;
        }),
    ];
    const processRowUpdate = (newRow, oldRow) => {
        debug && console.log(`processRowUpdate got `, { newRow, oldRow });
        const idx = observationMetricItems.findIndex((item) => item.id === newRow.id);
        debug && console.log('processRowUpdate edit ', { idx, observationMetricItems });
        if (observationHistoryStore) {
            const idx = observationHistoryStore.timestamp.findIndex((t) => newRow.timestamp === t);
            if (idx !== -1 && observationHistory) {
                const { timestamp, localTimeStr, sk, id, pk, type, ..._stateObject } = newRow;
                const inserts = [{ timestamp, state: _stateObject }];
                const update = observationHistoryStore.splice(idx, 1, inserts);
                const observationHistoryClone = new ObservationHistory({ ...observationHistory, observedHistory: update });
                setObservationHistory(observationHistoryClone);
                debug && console.log('processRowUpdate spliced', observationHistoryClone);
            }
            else {
                debug && console.log('processRowUpdate no idx for', newRow);
            }
        }
        return newRow;
    };
    const tableRowHeight = 24;
    const dataGridProps = {
        columns,
        columnHeaderHeight: tableRowHeight,
        rowHeight: tableRowHeight,
        rows: observationMetricItems,
        loading: loadingObjectHistory,
        columnVisibilityModel,
        autoHeight: true,
        processRowUpdate,
        onProcessRowUpdateError: (e) => {
            console.log(e.message);
        },
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
    };
    const setPlainObjectsInParent = (observations) => {
        const historyStoreClone = new HistoryStore({ ...observationHistoryStore });
        debug && console.log('TEST setPlainObjectsInParent ', observations);
        observations.forEach((observation) => {
            const { timestamp, type, sk, pk, id, name, ...others } = commonObjectTypeToVOB(observation);
            const duckTypedOthers = ducktypeStringMap(others);
            //const tempTimestamp = timestamp ? new Date(timestamp as string).valueOf() : Date.now()
            const timestampNumber = parseInt(timestamp ?? '');
            const timestampFromNumber = timestampNumber ? new Date(timestampNumber).valueOf() : -Infinity;
            debug && console.log('TEST Timestamp Number', timestampFromNumber);
            const timestampFromString = timestamp ? new Date(timestamp).valueOf() : Date.now();
            debug && console.log('TEST Timestamp String', timestampFromString);
            const processedTimestamp = Math.max(timestampFromNumber, isNaN(timestampFromString) ? -Infinity : timestampFromString);
            debug && console.log('TEST Processed Timestamp', processedTimestamp);
            const existingTimestampIdx = observation.timestamp && historyStoreClone.timestamp?.indexOf(processedTimestamp);
            if (existingTimestampIdx !== -1) {
                // replace
            }
            else if (observationHistory) {
                debug && console.log(`observation`, observation);
                historyStoreClone.push({ timestamp: processedTimestamp, state: duckTypedOthers });
                setObservationHistory(new ObservationHistory({ ...observationHistory, observedHistory: historyStoreClone }));
            }
        });
    };
    const { keys, vobs: exportVobs } = getExportVOBs({
        actualReadingPrefix,
        combined,
        objectHistory,
        observationHistory,
        observationHistoryStore,
        observationMetricItems,
        pseudoTypeId,
        matchedActualsCount,
        setMatchedActualsCount,
    });
    debug && console.log(`fileSelectRef current `, fileSelectRef?.current);
    return (_jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', gap: 1, children: [controls.addControl, controls.deleteKeyControl, controls.viewInComparison] }), stateObjectTable, querying ? (_jsx(Skeleton, { variant: 'rounded', height: '8em' })) : (_jsx(StripedDataGrid, { ...dataGridProps }, 'ObservationCardGrid')), _jsxs(Stack, { direction: 'row', gap: 1, children: [saveButton, _jsx(SimpleIOModal, { fileSelectRef: fileSelectRef,
                        columnIndexedKeys: ['timestamp', ...keys],
                        plainObjects: observationMetricItems,
                        allowIOTypeToggle: true,
                        initialIOType: IOType.EXPORT,
                        setPlainObjectsInParent,
                        sheets: [{ sheetName: `${viewObject.matchedPrimary?.name} Observations`, headers: ['timestamp', ...keys] }] }, 'SimpleIOModal'), controls.toggleCombinedControl, downloadButton] }), _jsxs(Grid, { container: true, children: [_jsxs(Grid, { size: {
                            md: 12,
                            xl: 6,
                        }, children: [viewObject.matchedPrimary && gapiIsPresent() ? (_jsx(Grid, { container: true, direction: 'column', children: _jsx(Grid, { size: {
                                        md: 12,
                                    }, children: _jsx(Grid, { size: {
                                            md: 12,
                                        } }, 'ExportGridContainerItem2') }, 'ExportGridContainerItem1') }, 'ExportGridContainer')) : (_jsx(Typography, { children: "Waiting for GAPI" })), observationHistory?.googleSheet?.spreadsheetName && _jsx(Typography, { children: `last sheet: ${observationHistory?.googleSheet?.spreadsheetName}` })] }, 'ExportGrid'), _jsx(Grid, {}, 'download')] }, 'Export')] }, 'ObservationCard'));
}
